import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatPKR, LOGO_URL } from "@/lib/api";
import { Printer, ArrowLeft } from "lucide-react";
import { describeConfig } from "@/lib/beddingConfig";

const COURIER_INFO = {
  trax: {
    label: "TRAX Logistics",
    tagline: "Same-day & Express Pakistan",
    accent: "#8DA08B",
  },
  postex: {
    label: "PostEx Courier",
    tagline: "Nationwide COD Delivery",
    accent: "#C98E8E",
  },
};

// Simple, print-friendly visual barcode from the AWB string.
function VisualBarcode({ value }) {
  const bars = [];
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    // Use char code to vary bar widths: produce 4 bars per char
    bars.push(((code & 0b11) + 1));
    bars.push(((code >> 2) & 0b11) + 1);
    bars.push(((code >> 4) & 0b11) + 1);
    bars.push(1);
  }
  return (
    <div className="flex items-end h-16 gap-[1px] my-3" aria-hidden>
      {bars.map((w, i) => (
        <div
          key={i}
          style={{ width: `${w}px`, height: "100%", background: i % 3 === 0 ? "#2B2D2A" : (w % 2 ? "#2B2D2A" : "transparent") }}
        />
      ))}
    </div>
  );
}

export default function AwbLabel() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [awb, setAwb] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get(`/admin/orders/${id}`),
      api.get(`/admin/orders/${id}/awb`).catch(() => ({ data: null })),
    ]).then(([o, a]) => {
      setOrder(o.data);
      setAwb(a.data);
    }).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="p-10 text-ink-soft">Loading…</div>;
  if (!order) return <div className="p-10 text-ink-soft">Order not found</div>;
  if (!awb) return (
    <div className="p-10 text-center">
      <h2 className="font-display text-2xl text-ink">No AWB generated for this order yet</h2>
      <Link to="/admin/logistics" className="inline-block mt-4 text-sage uppercase tracking-widest text-xs">← Back to Logistics</Link>
    </div>
  );

  const info = COURIER_INFO[awb.courier];
  const itemCount = order.items.reduce((s, i) => s + i.quantity, 0);

  return (
    <div className="bg-cream-warm min-h-screen py-8 print:bg-white print:py-0" data-testid="awb-label-page">
      <div className="max-w-3xl mx-auto px-6">
        <div className="flex justify-between items-center mb-4 print:hidden">
          <Link to="/admin/logistics" className="inline-flex items-center gap-2 text-xs text-ink-soft uppercase tracking-widest" data-testid="awb-back">
            <ArrowLeft size={14} /> Back to Logistics
          </Link>
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 bg-sage hover:bg-sage-dark text-cream px-4 py-2 text-xs uppercase tracking-widest transition-colors"
            data-testid="awb-print"
          >
            <Printer size={14} /> Print Label
          </button>
        </div>

        {/* Label */}
        <div className="bg-white border-2 border-ink p-6 print:border-2" data-testid="awb-label">
          {/* Header strip */}
          <div className="flex justify-between items-center pb-3 border-b-2 border-ink">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-ink-soft">Air Waybill</div>
              <div className="font-display text-3xl text-ink leading-tight">{info.label}</div>
              <div className="text-[11px] text-ink-soft">{info.tagline}</div>
            </div>
            <div className="text-right">
              <img src={LOGO_URL} alt="" className="h-12 w-12 inline-block object-contain" />
              <div className="font-display text-sm text-ink mt-1">Bedding Lounge</div>
            </div>
          </div>

          {/* AWB number + barcode */}
          <div className="py-4 border-b-2 border-ink">
            <div className="text-[10px] uppercase tracking-[0.25em] text-ink-soft">AWB Tracking Number</div>
            <div className="font-mono text-3xl text-ink tracking-wider">{awb.awb_number}</div>
            <VisualBarcode value={awb.awb_number} />
          </div>

          {/* From / To */}
          <div className="grid grid-cols-2 gap-6 py-4 border-b-2 border-ink">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-ink-soft mb-1">From (Shipper)</div>
              <div className="font-semibold text-ink">Bedding Lounge</div>
              <div className="text-sm text-ink-soft leading-snug">
                Lahore, Punjab, Pakistan<br />
                +92 335 4233258<br />
                Beddinglounge@gmail.com
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-ink-soft mb-1">To (Consignee)</div>
              <div className="font-semibold text-ink">{order.customer.full_name}</div>
              <div className="text-sm text-ink-soft leading-snug">
                {order.customer.address_line1}{order.customer.address_line2 ? `, ${order.customer.address_line2}` : ""}<br />
                {order.customer.city}, {order.customer.province} {order.customer.postal_code}<br />
                {order.customer.phone}<br />
                {order.customer.email}
              </div>
            </div>
          </div>

          {/* Shipment details */}
          <div className="grid grid-cols-3 gap-3 py-4 border-b-2 border-ink text-sm">
            <Box label="Pieces" value={itemCount} />
            <Box label="Weight (kg)" value={awb.weight_kg} />
            <Box label="Service" value="COD" />
          </div>

          {/* COD callout */}
          <div className="py-4 border-b-2 border-ink flex justify-between items-center">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-ink-soft">Collect Cash on Delivery</div>
              <div className="font-display text-3xl text-ink">{formatPKR(awb.cod_amount)}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.25em] text-ink-soft">Order</div>
              <div className="font-semibold text-ink">{order.order_number}</div>
            </div>
          </div>

          {/* Item list */}
          <div className="py-4">
            <div className="text-[10px] uppercase tracking-[0.25em] text-ink-soft mb-2">Contents</div>
            <ul className="space-y-1">
              {order.items.map((it, i) => (
                <li key={i} className="text-sm text-ink flex justify-between">
                  <span>{it.name} {describeConfig(it.config) && <span className="text-ink-soft">· {describeConfig(it.config)}</span>}</span>
                  <span className="text-ink-soft">×{it.quantity}</span>
                </li>
              ))}
            </ul>
            {awb.notes && (
              <div className="mt-3 text-xs italic text-ink-soft">Note: {awb.notes}</div>
            )}
          </div>

          <div className="pt-3 border-t-2 border-ink flex justify-between items-center text-[10px] uppercase tracking-widest text-ink-soft">
            <span>Generated {new Date(awb.created_at).toLocaleString("en-PK")}</span>
            <span>This is not an invoice</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function Box({ label, value }) {
  return (
    <div className="border border-ink/30 p-3 text-center">
      <div className="text-[9px] uppercase tracking-[0.2em] text-ink-soft">{label}</div>
      <div className="font-display text-2xl text-ink leading-tight mt-1">{value}</div>
    </div>
  );
}
